import z from "zod";
import {
  OfferIdScheme,
  ProductGroupIdScheme,
  ProductIdScheme,
  ProductVariantIdScheme,
} from "./ids";
import { MoneyScheme } from "./money";
import { OpeningHoursScheme } from "./openingHours";
import { OfferType } from "./enums/OfferType";
import { CatalogOrigin } from "./enums/CatalogOrigin";

export const OfferTargetScheme = z.object({
  productIds: z.array(ProductIdScheme),
  groupIds: z.array(ProductGroupIdScheme),
});

export const OfferScheduleScheme = z.object({
  startsAt: z.iso.date().nullable(),
  endsAt: z.iso.date().nullable(),
  hours: OpeningHoursScheme.nullable(),
});

export const AppliedOfferScheme = z.object({
  offerId: OfferIdScheme,
  name: z.string(),
  receiptText: z.string().nullable().optional(),
  type: z.enum(OfferType),
});

const OfferBaseScheme = z.object({
  id: OfferIdScheme,
  name: z.string(),
  receiptText: z.string().nullable().optional(),
  enabled: z.boolean(),
  priority: z.number(),
  schedule: OfferScheduleScheme.nullable(),
  isDeleted: z.boolean().optional(),
  origin: z.enum(CatalogOrigin).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const OfferFixedPriceEntryScheme = z.object({
  productId: ProductIdScheme,
  variantId: ProductVariantIdScheme.nullable(),
  unitPrice: MoneyScheme.nullable(),
  takeawayPrice: MoneyScheme.nullable().optional(),
});

export const OfferComboSlotScheme = z.object({
  target: OfferTargetScheme,
  quantity: z.number(),
});

export const OfferBuyXPayYScheme = OfferBaseScheme.extend({
  type: z.literal(OfferType.BUY_X_PAY_Y),
  target: OfferTargetScheme,
  buyQuantity: z.number(),
  payQuantity: z.number(),
});

export const OfferQuantityPriceScheme = OfferBaseScheme.extend({
  type: z.literal(OfferType.QUANTITY_PRICE),
  target: OfferTargetScheme,
  quantity: z.number(),
  price: MoneyScheme,
  takeawayPrice: MoneyScheme.nullable().optional(),
});

export const OfferFixedPriceScheme = OfferBaseScheme.extend({
  type: z.literal(OfferType.FIXED_PRICE),
  target: OfferTargetScheme,
  unitPrice: MoneyScheme.nullable(),
  takeawayPrice: MoneyScheme.nullable().optional(),
  prices: z.array(OfferFixedPriceEntryScheme),
});

export const OfferPercentageScheme = OfferBaseScheme.extend({
  type: z.literal(OfferType.PERCENTAGE),
  target: OfferTargetScheme,
  percentage: z.number(),
});

export const OfferComboScheme = OfferBaseScheme.extend({
  type: z.literal(OfferType.COMBO),
  slots: z.array(OfferComboSlotScheme),
  price: MoneyScheme,
  takeawayPrice: MoneyScheme.nullable().optional(),
});

export const OfferDBScheme = z.discriminatedUnion("type", [
  OfferBuyXPayYScheme,
  OfferQuantityPriceScheme,
  OfferFixedPriceScheme,
  OfferPercentageScheme,
  OfferComboScheme,
]);

export const RestGetOffer = OfferDBScheme;

const offerWriteOmit = {
  id: true,
  createdAt: true,
  updatedAt: true,
  isDeleted: true,
} as const;

const offerCreateOmit = {
  id: true,
  createdAt: true,
  updatedAt: true,
  isDeleted: true,
} as const;

export const RestCreateOffer = z.discriminatedUnion("type", [
  OfferBuyXPayYScheme.omit(offerCreateOmit),
  OfferQuantityPriceScheme.omit(offerCreateOmit),
  OfferFixedPriceScheme.omit(offerCreateOmit),
  OfferPercentageScheme.omit(offerCreateOmit),
  OfferComboScheme.omit(offerCreateOmit),
]);

export const RestUpdateOffer = z.discriminatedUnion("type", [
  OfferBuyXPayYScheme.omit(offerWriteOmit).partial().required({ type: true }),
  OfferQuantityPriceScheme.omit(offerWriteOmit)
    .partial()
    .required({ type: true }),
  OfferFixedPriceScheme.omit(offerWriteOmit).partial().required({ type: true }),
  OfferPercentageScheme.omit(offerWriteOmit).partial().required({ type: true }),
  OfferComboScheme.omit(offerWriteOmit).partial().required({ type: true }),
]);

export type OfferTarget = z.infer<typeof OfferTargetScheme>;
export type OfferSchedule = z.infer<typeof OfferScheduleScheme>;
export type AppliedOffer = z.infer<typeof AppliedOfferScheme>;
export type OfferFixedPriceEntry = z.infer<typeof OfferFixedPriceEntryScheme>;
export type OfferComboSlot = z.infer<typeof OfferComboSlotScheme>;
export type DBOffer = z.infer<typeof OfferDBScheme>;
export type RestOffer = z.infer<typeof RestGetOffer>;
export type RestCreateOffer = z.infer<typeof RestCreateOffer>;
export type RestUpdateOffer = z.infer<typeof RestUpdateOffer>;
export type OfferBuyXPayY = z.infer<typeof OfferBuyXPayYScheme>;
export type OfferQuantityPrice = z.infer<typeof OfferQuantityPriceScheme>;
export type OfferFixedPrice = z.infer<typeof OfferFixedPriceScheme>;
export type OfferPercentage = z.infer<typeof OfferPercentageScheme>;
export type OfferCombo = z.infer<typeof OfferComboScheme>;
